import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export type PccDiagnosticReportReportType = 'laboratory' | 'radiology';
export type PccDiagnosticReportReportStatus = 'COMPLETED' | 'RESULTED' | 'CANCELLED';
export class PccDiagnosticReportParams {
	reportType?: PccDiagnosticReportReportType;

	reportStatuses?: Array<PccDiagnosticReportReportStatus | 'ALL'>;

	issuedStartDateTime?: string;

	issuedEndDateTime?: string;
}

/**
 * Represents the ordering practitioner details
 */
export class PccOrderingPractitioner {
	/**
	 * The practitioner's first name
	 */
	firstName: string;

	/**
	 * The practitioner's last name
	 */
	lastName: string;

	/**
	 * The practitioner's middle name
	 */
	middleName: string;

	/**
	 * National Provider ID for the practitioner
	 */
	npi: number;

	/**
	 * The unique identifier for the practitioner within the organization
	 */
	practitionerId: number;
}

/**
 * Represents the value quantity for test results
 */
export class PccValueQuantity {
	/**
	 * The unit of measurement for the value
	 */
	unitText: string;

	/**
	 * The numeric value of the test result
	 */
	value: string;
}

/**
 * Represents individual test result within a test set
 */
export class PccDiagnosticReportTestResult {
	/**
	 * The LOINC code for the test
	 */
	code: string;

	/**
	 * Description of the test code
	 */
	codeDescription: string;

	/**
	 * The coding system used (e.g., "LOINC")
	 */
	codeSystem: string;

	/**
	 * Additional comments about the test result
	 */
	comment: string;

	/**
	 * Unique identifier for the observation
	 */
	observationId: string;

	/**
	 * Name of the observation/test
	 */
	observationName: string;

	/**
	 * Reference range for normal values
	 */
	referenceRange: string;

	/**
	 * Status of the result (e.g., "Final")
	 */
	resultStatus: string;

	/**
	 * The value and unit of the test result
	 */
	valueQuantity: PccValueQuantity;

	/**
	 * Represents the details of the test result.
	 *
	 * SimpliScreen maps the test result to the following string for embedding in the RAG:
	 * codeDescription: {codeDescription}, code: {code}, codeSystem: {codeSystem}, comment: {comment}, observationName: {observationName}, referenceRange: {referenceRange}, resultStatus: {resultStatus}, valueQuantity: {valueQuantity.value}{valueQuantity.unitText}
	 */
	details: string;

	constructor(partial?: Partial<PccDiagnosticReportTestResult>) {
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '') {
		const details = [];
		if (this.codeDescription) details.push(`Code Description: ${this.codeDescription}`);
		if (this.code) details.push(`Code: ${this.code}`);
		if (this.codeSystem) details.push(`Code System: ${this.codeSystem}`);
		if (this.comment) details.push(`Comment: ${this.comment}`);
		if (this.observationName) details.push(`Observation Name: ${this.observationName}`);
		if (this.referenceRange) details.push(`Reference Range: ${this.referenceRange}`);
		if (this.resultStatus) details.push(`Result Status: ${this.resultStatus}`);
		if (this.valueQuantity) details.push(`Value Quantity: ${this.valueQuantity.value}${this.valueQuantity.unitText}`);
		return `${prefix}${details.join(', ')}`;
	}
}

/**
 * Represents a test set containing multiple test results
 */
export class PccDiagnosticReportTest {
	/**
	 * Represents the panel name. This field is only applicable to Laboratory reports.
	 */
	panelName: string;

	/**
	 * List of test results within this panel
	 */
	results: PccDiagnosticReportTestResult[];

	/**
	 * Represents the details of the test.
	 *
	 * SimpliScreen maps the test to the following string for embedding in the RAG:
	 * panelName: {panelName}, results: {results}
	 */
	details: string;

	constructor(partial?: Partial<PccDiagnosticReportTest>) {
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '') {
		const details = [];
		if (this.panelName) details.push(`Panel Name: ${this.panelName}`);
		return `${prefix}${details.join(', ')}\n${this.results.map((result) => result.getDetails(`${prefix}\t`)).join('\n')}`;
	}
}

/**
 * Represents a diagnostic report for a patient in PointClickCare
 *
 * SimpliScreen maps the diagnostic report to the following string for embedding in the RAG:
 * reportName: {reportName}, reportType: {reportType}, reportStatus: {reportStatus}, effectiveDateTime: {effectiveDateTime}, issuedDateTime: {issuedDateTime}, performingLaboratory: {performingLaboratory}, reportingLaboratory: {reportingLaboratory}, category: {category}, testSet: {testSet}
 * \t codeDescription: {codeDescription}, code: {code}, codeDescription: {codeDescription}, codeSystem: {codeSystem}, comment: {comment}, observationName: {observationName}, referenceRange: {referenceRange}, resultStatus: {resultStatus}, valueQuantity: {valueQuantity.value}{valueQuantity.unitText}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/diagnostic-reports?patientId={patientId}&reportType={laboratory|radiology}
 */
export class PccDiagnosticReport extends PccAuditInfo {
	/**
	 * Represents the category that the report belongs to. Multiple categories may be selected for a report. This field is only applicable to Laboratory reports. Possible values are:
	 * Chemistry, Hematology, Challenge, Semen Analysis, Serology, Cancer Screening Tests, Microbiology, Cytology, Anatomic Pathology, Blood Bank, Coagulation, Unknown Category
	 */
	category?: string[];

	/**
	 * Represents the date and time of the procedure or of specimen collection(s). The date and time are expressed in UTC in the following format YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	effectiveDateTime: string;

	/**
	 * Represents the date and time when the report was issued. The date and time are expressed in UTC in the following format YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	issuedDateTime: string;

	/**
	 * Details on the practitioner that ordered the diagnostic report
	 */
	orderingPractitioner: PccOrderingPractitioner;

	/**
	 * Represents the laboratory that performed the tests included on the report
	 */
	performingLaboratory: string;

	/**
	 * Represents if the report has a file attached to it. Possible values are 'true' or 'false'.
	 */
	reportFile: boolean;

	/**
	 * The unique identifier for the report. ReportId for laboratory reports is prefixed with "lab-" whereas for Radiology reports, it is prefixed with "img-".
	 */
	reportId: string;

	/**
	 * Represents the name of the report
	 */
	reportName: string;

	/**
	 * Represents the status of the report. Possible values are 'Completed', 'Resulted', and 'Cancelled'. When results for all tests included in the report are available, the status is 'Completed'.
	 */
	reportStatus: 'Completed' | 'Resulted' | 'Cancelled';

	/**
	 * Represents the type of report. Possible values are 'Laboratory' or 'Radiology'.
	 */
	reportType: PccDiagnosticReportReportType;

	/**
	 * Represents the laboratory that reported the results
	 */
	reportingLaboratory: string;

	/**
	 * List of test results reported
	 */
	testSet: PccDiagnosticReportTest[];

	/**
	 * Represents the details of the diagnostic report.
	 *
	 * SimpliScreen maps the diagnostic report to the following string for embedding in the RAG:
	 * reportName: {reportName}, reportType: {reportType}, reportStatus: {reportStatus}, effectiveDateTime: {effectiveDateTime}, issuedDateTime: {issuedDateTime}, performingLaboratory: {performingLaboratory}, reportingLaboratory: {reportingLaboratory}, category: {category}, testSet: {testSet}
	 * \t codeDescription: {codeDescription}, code: {code}, codeDescription: {codeDescription}, codeSystem: {codeSystem}, comment: {comment}, observationName: {observationName}, referenceRange: {referenceRange}, resultStatus: {resultStatus}, valueQuantity: {valueQuantity.value}{valueQuantity.unitText}
	*/
	details: string;

	constructor(partial?: Partial<PccDiagnosticReport>) {
		super();
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails(skipName = false) {
		const details = [`Data Type: Diagnostic Report`];
		if (this.reportId) details.push(`Report ID: ${this.reportId}`);
		if (this.reportName && !skipName) details.push(`Report Name: ${this.reportName}`);
		if (this.reportType) details.push(`Report Type: ${this.reportType}`);
		if (this.reportStatus) details.push(`Report Status: ${this.reportStatus}`);
		if (this.effectiveDateTime) details.push(`Effective Date Time: ${this.effectiveDateTime}`);
		if (this.issuedDateTime) details.push(`Issued Date Time: ${this.issuedDateTime}`);
		if (this.performingLaboratory) details.push(`Performing Laboratory: ${this.performingLaboratory}`);
		if (this.reportingLaboratory) details.push(`Reporting Laboratory: ${this.reportingLaboratory}`);
		if (this.category) details.push(`Category: ${this.category}`);
		return `${details.join(', ')}\n${this.testSet.map((test) => test.getDetails('\t')).join('\n')}`;
	}
}
